import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiPlus, FiSave, FiTrash2 } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, fileToBase64, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import { champFields, extractEntity, extractList } from '../utilities/tournamentsApi';
import {
  buildPlayerPayload,
  emptyPlayerForm,
  extractPlayer,
  extractPlayersPage,
  formatPlayerNumber,
  getPlayerPhotoUrl,
  getTeamPhotoUrl,
  isPlayerCaptain,
  playerAppChangePath,
  playerAppNewPath,
  playerCardPath,
  playerDisplayName,
  playerFromEntity,
  playerShirtNumber,
} from '../utilities/playersApi';
import PlayerFormFields from '../components/PlayerFormFields';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import CustomModal from '../components/CustomModal';
import defaultPlayerImage from '../images/defaultAvatar.png';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/disqualificationsManagement.css';
import '../styles/tournamentsManagement.css';
import '../styles/playersManagement.css';

export default function ManagePlayer() {
  const { playerId, teamId: teamContextId } = useParams();
  const isNew = !playerId;
  const dispatch = useDispatch();
  const history = useHistory();
  const { t, lang } = useI18n();
  const dateLocale = lang === 'en' ? 'en-GB' : 'ru-RU';
  const { champId, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('players');

  const [form, setForm] = useState(emptyPlayerForm);
  const [player, setPlayer] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [teamApplications, setTeamApplications] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [linking, setLinking] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [removeTeam, setRemoveTeam] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [unlinkProfile, setUnlinkProfile] = useState(null);
  const [unlinking, setUnlinking] = useState(false);
  const [captainTeamId, setCaptainTeamId] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [existingMatches, setExistingMatches] = useState(null);
  const [selectedExisting, setSelectedExisting] = useState(null);

  useEffect(() => {
    if (!teamContextId) {
      setTeamName('');
      setTeamApplications([]);
      return;
    }

    authorizedPost(dispatch, API_URLS.getCatalogTeam, { id: Number(teamContextId) })
      .then((response) => setTeamName(extractEntity(response.data).name || ''))
      .catch(() => setTeamName(''));

    if (!isNew) {
      setTeamApplications([]);
      return;
    }
    if (!champId && champsLoading) return;

    authorizedPost(dispatch, API_URLS.getAdminApplications, champFields(champId))
      .then((response) => {
        const apps = extractList(response.data);
        setTeamApplications(apps);
        const firstId = apps[0]?.id || apps[0]?.application_id;
        if (firstId) {
          setForm((prev) => prev.application_id ? prev : { ...prev, application_id: String(firstId) });
        }
      })
      .catch(() => setTeamApplications([]));
  }, [teamContextId, isNew, champId, champsLoading]);

  useEffect(() => {
    if (isNew) return;
    if (!champId && champsLoading) return;
    loadPlayer();
  }, [playerId, champId, champsLoading, isNew]);

  const loadPlayer = async () => {
    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminPlayer, {
        id: Number(playerId),
        player_id: Number(playerId),
      });
      const data = extractPlayer(response.data);
      setPlayer(data);
      setForm(playerFromEntity(data));
      const imageUrl = getPlayerPhotoUrl(data.image);
      setPhotoPreview(imageUrl ? `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}v=${Date.now()}` : '');
      setSelectedProfiles([]);
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('pdLoadError')), type: 'error' });
      setPlayer(null);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
    if (key === 'family' || key === 'name' || key === 'father_name') {
      setExistingMatches(null);
      setSelectedExisting(null);
    }
  };

  const handleCheckExisting = async () => {
    const family = form.family.trim();
    const name = form.name.trim();
    if (!family || !name) return;
    setCheckingExisting(true);
    setSelectedExisting(null);
    setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminPlayers, {
        ...champFields(champId),
        search: [family, name, form.father_name.trim()].filter(Boolean).join(' '),
        limit: 20,
        offset: 0,
      });
      const items = extractPlayersPage(response.data).items;
      const familyNorm = family.toLowerCase();
      const nameNorm = name.toLowerCase();
      const fatherNorm = form.father_name.trim().toLowerCase();
      setExistingMatches(items.filter((item) => {
        if (!String(item.family || '').toLowerCase().includes(familyNorm)) return false;
        if (!String(item.name || '').toLowerCase().includes(nameNorm)) return false;
        if (fatherNorm && !String(item.father_name || '').toLowerCase().includes(fatherNorm)) return false;
        return true;
      }));
    } catch (error) {
      setExistingMatches([]);
      setMessage({ text: getApiErrorMessage(error, t('pmSearchError')), type: 'error' });
    } finally {
      setCheckingExisting(false);
    }
  };

  const handleSelectExisting = (item) => {
    setSelectedExisting(item);
    setExistingMatches(null);
    setPhotoError('');
    setErrors((prev) => {
      const next = { ...prev };
      delete next.family;
      delete next.name;
      delete next.birthday;
      return next;
    });
  };

  const handleClearExisting = () => {
    setSelectedExisting(null);
    setExistingMatches(null);
  };

  const validate = (requirePhoto) => {
    const nextErrors = {};
    if (!selectedExisting) {
      if (!form.family.trim()) nextErrors.family = t('epmFamilyPh');
      if (!form.name.trim()) nextErrors.name = t('epmNamePh');
      if (!form.birthday) nextErrors.birthday = t('epmRequiredFields');
      if (requirePhoto && !photoFile && !photoPreview) {
        setPhotoError(t('stNeedImage'));
      }
    }
    if (isNew && teamContextId) {
      if (!form.application_id) nextErrors.application_id = t('apaPickApp');
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0 && !(!selectedExisting && requirePhoto && !photoFile && !photoPreview);
  };

  const handleSave = async () => {
    if (!validate(isNew && !selectedExisting)) return;
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      if (isNew && selectedExisting && teamContextId) {
        const existingId = Number(selectedExisting.id || selectedExisting.player_id);
        if (!existingId) {
          setMessage({ text: t('pdNotFound'), type: 'error' });
          setSaving(false);
          return;
        }
        const payload = {
          id: existingId,
          player_id: existingId,
          action: 'add',
          team_id: Number(teamContextId),
          application_id: Number(form.application_id),
        };
        const number = playerShirtNumber(form.number);
        if (number != null) payload.number = number;
        const response = await authorizedPost(dispatch, API_URLS.changePlayerApplication, payload);
        const saved = extractPlayer(response.data);
        history.replace(playerCardPath(saved.id || existingId, teamContextId));
        return;
      }

      const imageBase64 = photoFile ? await fileToBase64(photoFile) : undefined;
      const payload = buildPlayerPayload(form, {
        imageBase64,
        champId,
        playerId,
        isNew,
        teamId: teamContextId,
      });

      const response = await authorizedPost(
        dispatch,
        isNew ? API_URLS.addPlayer : API_URLS.changePlayer,
        payload,
      );
      const saved = extractPlayer(response.data);
      setMessage({ text: isNew ? t('epmCreated') : t('epmUpdated'), type: 'success' });
      if (isNew && saved.id) {
        history.replace(playerCardPath(saved.id, teamContextId));
      } else {
        setPlayer(saved);
        setForm(playerFromEntity(saved));
        setPhotoFile(null);
        const imageUrl = getPlayerPhotoUrl(saved.image);
        setPhotoPreview(imageUrl ? `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}v=${Date.now()}` : '');
      }
    } catch (error) {
      setMessage({
        text: getApiErrorMessage(
          error,
          selectedExisting ? t('apaAddError') : (isNew ? t('epmCreateError') : t('epmUpdateError')),
        ),
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveApplication = async () => {
    if (!removeTeam) return;
    const teamId = Number(removeTeam.team_id);
    const applicationId = Number(removeTeam.application_id);
    if (!playerId || !teamId || !applicationId) {
      setMessage({ text: t('pdRemoveError'), type: 'error' });
      return;
    }
    setRemoving(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.changePlayerApplication, {
        id: Number(playerId),
        player_id: Number(playerId),
        action: 'remove',
        team_id: teamId,
        application_id: applicationId,
      });
      setPlayer(extractPlayer(response.data));
      setRemoveTeam(null);
      setMessage({ text: t('pdRemoved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('pdRemoveError')), type: 'error' });
    } finally {
      setRemoving(false);
    }
  };

  const openTeamCard = (item) => {
    if (!item?.team_id) return;
    history.push(`/teams_management/${item.team_id}`);
  };

  const toggleProfile = (id) => {
    setSelectedProfiles((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
  };

  const handleUnlinkProfile = async () => {
    if (!unlinkProfile) return;
    setUnlinking(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.unlinkPlayerProfiles, {
        id: Number(playerId),
        player_id: Number(playerId),
        linked_player_id: Number(unlinkProfile.id),
      });
      setPlayer(extractPlayer(response.data));
      setUnlinkProfile(null);
      setMessage({ text: t('plProfilesUnlinked'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('plProfilesUnlinkError')), type: 'error' });
    } finally {
      setUnlinking(false);
    }
  };

  const handleToggleCaptain = async (item) => {
    if (!item?.team_id || captainTeamId) return;
    const isCaptain = isPlayerCaptain(item, player);
    setCaptainTeamId(item.team_id);
    setMessage({ text: '', type: '' });
    try {
      const payload = {
        id: Number(playerId),
        player_id: Number(playerId),
        team_id: Number(item.team_id),
      };
      if (!isCaptain) {
        const number = playerShirtNumber(item.number);
        if (number != null) payload.number = number;
      }
      const response = await authorizedPost(
        dispatch,
        isCaptain ? API_URLS.deletePlayerCaptain : API_URLS.addPlayerCaptain,
        payload,
      );
      const next = extractPlayer(response.data);
      const teamId = Number(item.team_id);
      const nextCaptain = isCaptain ? 0 : 1;
      next.teams = next.teams.map((row) => ({
        ...row,
        capitan: Number(row.team_id) === teamId ? nextCaptain : row.capitan,
      }));
      next.related_teams = next.related_teams.map((row) => ({
        ...row,
        capitan: Number(row.team_id) === teamId ? nextCaptain : row.capitan,
      }));
      setPlayer(next);
      setMessage({ text: isCaptain ? t('plCaptainRemoved') : t('plCaptainSet'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, isCaptain ? t('plCaptainRemoveError') : t('plCaptainError')), type: 'error' });
    } finally {
      setCaptainTeamId(null);
    }
  };

  const handleLinkProfiles = async () => {
    if (!selectedProfiles.length) return;
    setLinking(true);
    setMessage({ text: '', type: '' });
    try {
      let latest = player;
      for (const linkedId of selectedProfiles) {
        const response = await authorizedPost(dispatch, API_URLS.linkPlayerProfiles, {
          id: Number(playerId),
          player_id: Number(playerId),
          linked_player_id: Number(linkedId),
        });
        latest = extractPlayer(response.data);
      }
      setPlayer(latest);
      setSelectedProfiles([]);
      setMessage({ text: t('plProfilesLinked'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('plProfilesLinkError')), type: 'error' });
    } finally {
      setLinking(false);
    }
  };

  const teams = player?.teams || [];
  const transfers = player?.transfers || [];
  const relatedProfiles = player?.related_profiles || [];
  const possibleProfiles = player?.possible_profiles || [];

  const teamColumns = [
    {
      key: 'team',
      label: t('pdTeam'),
      className: 'st-cell-name',
      render: (item) => (
        <span className="pl-team-cell">
          <img
            src={getTeamPhotoUrl(item.team_image) || defaultTeamLogo}
            alt=""
            className="pl-team-logo"
            onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
          />
          <span>{item.team_name || '—'}</span>
          {isPlayerCaptain(item, player) ? <span className="pl-captain-badge">{t('plCaptain')}</span> : null}
        </span>
      ),
    },
    {
      key: 'application',
      label: t('pdApp'),
      className: 'st-cell-muted',
      render: (item) => item.application_name || '—',
    },
    {
      key: 'number',
      label: t('pdNumber'),
      className: 'pl-cell-num',
      render: (item) => formatPlayerNumber(item.number),
    },
    {
      key: 'actions',
      label: t('pdActions'),
      className: 'st-cell-actions',
      render: (item) => {
        const captain = isPlayerCaptain(item, player);
        return (
          <span className="pl-row-actions">
            <button
              type="button"
              className={`st-icon-btn ${captain ? 'is-active' : ''}`}
              title={captain ? t('plRemoveCaptain') : t('plMakeCaptain')}
              disabled={captainTeamId === item.team_id}
              onClick={(event) => {
                event.stopPropagation();
                handleToggleCaptain(item);
              }}
            >
              <img src="/images/captain.png" alt="" className="pl-captain-icon" />
            </button>
            <Link
              to={playerAppChangePath(playerId, item.team_id, item.application_id, teamContextId)}
              className="st-icon-btn"
              title={t('plChangeApp')}
              onClick={(event) => event.stopPropagation()}
            >
              ✎
            </Link>
            <button
              type="button"
              className="st-icon-btn danger"
              title={t('pdRemoveTitle')}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setRemoveTeam(item);
              }}
            >
              <FiTrash2 />
            </button>
          </span>
        );
      },
    },
  ];

  const profileColumns = (selectable, unlinkable) => [
    ...(selectable ? [{
      key: 'pick',
      label: '',
      className: 'pl-cell-check',
      render: (item) => (
        <input
          type="checkbox"
          checked={selectedProfiles.includes(item.id)}
          onChange={() => toggleProfile(item.id)}
          onClick={(event) => event.stopPropagation()}
        />
      ),
    }] : []),
    {
      key: 'name',
      label: t('pmFio'),
      className: 'st-cell-name',
      render: (item) => (
        <span className="pl-team-cell">
          <img
            src={getPlayerPhotoUrl(item.image) || defaultPlayerImage}
            alt=""
            className="pl-profile-photo"
            onError={(event) => { event.currentTarget.src = defaultPlayerImage; }}
          />
          <span>{playerDisplayName(item) || '—'}</span>
        </span>
      ),
    },
    {
      key: 'champ',
      label: t('championship'),
      className: 'pl-cell-champ',
      render: (item) => item.championat_name || '—',
    },
    ...(unlinkable ? [{
      key: 'actions',
      label: t('pdActions'),
      className: 'st-cell-actions',
      render: (item) => (
        <button
          type="button"
          className="st-icon-btn danger"
          title={t('plUnlinkProfile')}
          onClick={(event) => {
            event.stopPropagation();
            setUnlinkProfile(item);
          }}
        >
          <FiTrash2 />
        </button>
      ),
    }] : []),
  ];

  const crumbs = teamContextId
    ? [
      { label: t('navTeams'), to: '/teams_management' },
      { label: teamName || t('tmEditTeam'), to: `/teams_management/${teamContextId}` },
      { label: isNew ? t('plNewPlayer') : (playerDisplayName(form) || playerDisplayName(player) || t('plEditPlayer')) },
    ]
    : [
      { label: t('navPlayers'), to: '/players_management' },
      { label: isNew ? t('plNewPlayer') : (playerDisplayName(form) || playerDisplayName(player) || t('plEditPlayer')) },
    ];

  const saveButton = canWrite ? (
    <button
      type="button"
      className="st-btn st-btn-primary"
      disabled={loading || saving}
      onClick={handleSave}
    >
      <FiSave /> {saving ? t('trLoading') : (isNew ? (selectedExisting ? t('apaAdd') : t('epmCreateBtn')) : t('stdSaveChanges'))}
    </button>
  ) : null;

  const formBlock = (
    <PlayerFormFields
      form={form}
      errors={errors}
      photoPreview={photoPreview}
      photoError={photoError}
      saving={saving}
      photoRequired={isNew && !selectedExisting}
      showTeamBinding={Boolean(isNew && teamContextId)}
      applications={teamApplications}
      teamName={teamName}
      showExistingCheck={Boolean(isNew && teamContextId)}
      canCheckExisting={Boolean(form.family.trim() && form.name.trim())}
      checkingExisting={checkingExisting}
      existingMatches={existingMatches}
      selectedExisting={selectedExisting}
      onCheckExisting={handleCheckExisting}
      onSelectExisting={handleSelectExisting}
      onClearExisting={handleClearExisting}
      onFieldChange={updateField}
      onPhotoChange={(file, error, preview) => {
        if (error) {
          setPhotoError(error);
          return;
        }
        setPhotoError('');
        setPhotoFile(file);
        setPhotoPreview(preview);
      }}
    />
  );

  if (!isNew && loading) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page tm-page">
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('pdLoading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (isNew) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs items={crumbs} />
            </div>
          </div>

          {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

          <div className="st-scroll">
            <div className="st-content">
              <div className="st-block">
                <h2 className="st-block-header">{t('plNewPlayer')}</h2>
                {formBlock}
              </div>
            </div>
          </div>

          <div className="st-actions">
            {saveButton}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page tm-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={crumbs} />
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="dq-split tm-split pl-split">
            <section className="st-block dq-panel tm-form-panel">
              <h2 className="st-block-header">{t('plEditPlayer')}</h2>
              <div className="dq-panel-body">
                {formBlock}
              </div>
              <div className="st-actions">
                {saveButton}
              </div>
            </section>

            <div className="dq-stack pl-stack">
              <section className="st-block dq-panel">
                <h2 className="st-block-header">{t('plTeamsBlock')}</h2>
                <div className="dq-panel-body">
                  <ReorderableTable
                    tableClassName="st-table-player-apps"
                    columns={teamColumns}
                    items={teams}
                    getRowKey={(item, index) => `${item.team_id}_${item.application_id}_${index}`}
                    reorderMode={false}
                    onRowClick={openTeamCard}
                    empty={<div className="st-empty"><p>{t('pdEmptyApps')}</p></div>}
                  />
                </div>
                <div className="st-actions">
                  {canWrite ? (
                  <Link to={playerAppNewPath(playerId, teamContextId)} className="st-btn st-btn-primary">
                    <FiPlus /> {t('plAddApp')}
                  </Link>
                  ) : null}
                </div>
              </section>

              <section className="st-block dq-panel">
                <h2 className="st-block-header">{t('plTransfersBlock')}</h2>
                <div className="dq-panel-body">
                  {transfers.length ? (
                    <div className="pl-transfer-list">
                      {transfers.map((item) => {
                        const applicationName = item.application?.name || item.application_name || '';
                        const typeLabel = item.type_name
                          || (item.type === 'application_add' ? t('plTransferAddApp') : item.type)
                          || '—';
                        return (
                          <div key={item.id} className="pl-transfer-row">
                            <div className="pl-transfer-date">
                              {item.date ? new Date(item.date).toLocaleDateString(dateLocale) : '—'}
                            </div>
                            <div className="pl-transfer-type">
                              <span>{typeLabel}</span>
                              {applicationName ? (
                                <span className="pl-transfer-app">{applicationName}</span>
                              ) : null}
                            </div>
                            <div className="pl-transfer-teams">
                              {item.from_team ? (
                                <>
                                  <span className="pl-transfer-team">
                                    <img
                                      src={getTeamPhotoUrl(item.from_team.image) || defaultTeamLogo}
                                      alt=""
                                      onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                                    />
                                    <span>{item.from_team.name || '—'}</span>
                                  </span>
                                  {item.to_team ? <span className="pl-transfer-arrow">→</span> : null}
                                </>
                              ) : null}
                              {item.to_team ? (
                                <span className="pl-transfer-team">
                                  <img
                                    src={getTeamPhotoUrl(item.to_team.image) || defaultTeamLogo}
                                    alt=""
                                    onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                                  />
                                  <span>{item.to_team.name || '—'}</span>
                                </span>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="st-empty"><p>{t('plEmptyTransfers')}</p></div>
                  )}
                </div>
              </section>

              <section className="st-block dq-panel">
                <h2 className="st-block-header">{t('plRelatedProfiles')}</h2>
                <div className="dq-panel-body">
                  <ReorderableTable
                    tableClassName="st-table-profiles-related"
                    columns={profileColumns(false, true)}
                    items={relatedProfiles}
                    getRowKey={(item) => item.id}
                    reorderMode={false}
                    getRowHref={(item) => (item.id ? playerCardPath(item.id) : null)}
                    empty={<div className="st-empty"><p>{t('plEmptyRelated')}</p></div>}
                  />
                </div>
              </section>

              <section className="st-block dq-panel">
                <h2 className="st-block-header">{t('plPossibleProfiles')}</h2>
                <div className="dq-panel-body">
                  <ReorderableTable
                    tableClassName="st-table-profiles"
                    columns={profileColumns(true)}
                    items={possibleProfiles}
                    getRowKey={(item) => item.id}
                    reorderMode={false}
                    empty={<div className="st-empty"><p>{t('plEmptyPossible')}</p></div>}
                  />
                </div>
                <div className="st-actions">
                  {canWrite ? (
                  <button
                    type="button"
                    className="st-btn st-btn-primary"
                    disabled={!selectedProfiles.length || linking}
                    onClick={handleLinkProfiles}
                  >
                    {linking ? t('trLoading') : t('plLinkSelected')}
                  </button>
                  ) : null}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={Boolean(removeTeam)}
        closeModal={() => !removing && setRemoveTeam(null)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('pdRemoveTitle')}</div>
          <p>
            {t('pdRemoveConfirm', {
              app: removeTeam?.application_name || t('pdApp'),
              team: removeTeam?.team_name || t('pdTeam'),
            })}
          </p>
          <div className="st-delete-actions">
            <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setRemoveTeam(null)} disabled={removing}>
              {t('cancel')}
            </button>
            <button type="button" className="btn-modal btn-danger-modal" onClick={handleRemoveApplication} disabled={removing}>
              {removing ? t('stSaving') : t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={Boolean(unlinkProfile)}
        closeModal={() => !unlinking && setUnlinkProfile(null)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('plUnlinkProfile')}</div>
          <p>
            {t('plUnlinkConfirm', { name: playerDisplayName(unlinkProfile) || t('plRelatedProfiles') })}
          </p>
          <div className="st-delete-actions">
            <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setUnlinkProfile(null)} disabled={unlinking}>
              {t('cancel')}
            </button>
            <button type="button" className="btn-modal btn-danger-modal" onClick={handleUnlinkProfile} disabled={unlinking}>
              {unlinking ? t('stSaving') : t('plUnlinkProfile')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
